<template>
  <h3 class="payment-title">{{ t("selectPaymentMethod") }}</h3>
  <div class="order-cta-container">
    <div class="payment-methods-container">
      <div class="payment-methods">
        <div class="extra-options">
          <template v-for="method in paymentMethods" :key="method.value">
            <label
              :class="['extra-checkbox', { 'btn-disabled': !canCheckout }]"
              v-if="method.enabled"
            >
              <input
                type="radio"
                name="payment"
                :value="method.value"
                :disabled="!canCheckout"
                v-model="selectedPayment"
              />
              <div v-if="method.image">
                <img :src="method.image" :alt="t('paymentMethodLogo')" />
              </div>
              <div v-else>
                <component :is="method.icon" />
                <span>{{ method.label }}</span>
              </div>
            </label>
          </template>
        </div>
      </div>
    </div>
    <div class="checkout-btn-wrapper">
      <button
        :class="['btn', 'order-checkout-btn', { 'btn-disabled': !canCheckout }]"
        @click="checkoutModal"
        :disabled="!canCheckout"
      >
        <div class="checkout-price">
          <IconCurrencyBaht
            :width="12"
            :height="18"
            :stroke-width="2"
            stroke="currentColor"
          />
          <span :class="{ 'price-updated': animateAmount }">{{
            totalAmount.toFixed(2)
          }}</span>
        </div>
        <div>
          <IconCheckoutArrow
            :width="24"
            :height="24"
            stroke="currentColor"
            :stroke-width="2"
          />
          <span>{{ t("pay") }} {{ t("now") }}</span>
        </div>
      </button>
    </div>
  </div>

  <CheckoutModal
    :is-open="openCheckoutModal"
    @update:isOpen="openCheckoutModal = $event"
  />
</template>

<script setup>
import { computed, defineEmits, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useOrderStore } from "@/stores/order";
import { useAnimatedValue } from "@/composables/useAnimatedValue";
import { useOrderGuard } from "@/composables/useOrderGuard";
import { useCheckoutState } from "@/composables/useCheckoutState";
import { PROMPTPAY_IMAGE } from "@/constants/image";
import {
  PAYMENT_METHOD_CASH,
  PAYMENT_METHOD_PROMPTPAY,
  PAYMENT_METHOD_CARD,
} from "@/constants/order";
import IconCash from "@/components/icons/iconCash.vue";
import IconCard from "@/components/icons/iconCard.vue";
import IconCheckoutArrow from "@/components/icons/iconCheckoutArrow.vue";
import IconCurrencyBaht from "@/components/icons/iconCurrencyBaht.vue";
import CheckoutModal from "@/components/order/modals/checkoutModal.vue";

const emit = defineEmits(["update:openTable"]);
const { t } = useI18n();

const paymentMethods = [
  {
    value: PAYMENT_METHOD_CASH,
    label: t("paymentCash"),
    icon: IconCash,
    enabled: true,
  },
  {
    value: PAYMENT_METHOD_PROMPTPAY,
    label: t("paymentPromptPay"),
    image: PROMPTPAY_IMAGE,
    enabled: true,
  },
  {
    value: PAYMENT_METHOD_CARD,
    label: t("paymentCard"),
    icon: IconCard,
    enabled: false,
  },
];

const {
  getCurrentTable,
  getPaymentMethod,
  getTotalAmount,
  updatePaymentMethod,
} = useOrderStore();

const paymentMethod = computed(() => getPaymentMethod());
const selectedTable = computed(() => getCurrentTable());
const totalAmount = computed(() => getTotalAmount());
const { canCheckout, openCheckoutModal, setOpenCheckoutModal } =
  useCheckoutState();

const [, animateAmount] = useAnimatedValue(totalAmount);

const selectedPayment = ref(paymentMethod.value);

// Guard to prevent payment changes during processing
const { canModifyOrder } = useOrderGuard(() => {
  setOpenCheckoutModal(true);
});

const checkoutModal = () => {
  if (!canModifyOrder()) return;
  if (selectedTable.value == null) {
    emit("update:openTable");
    return;
  }
  openCheckoutModal.value = true;
};

watch(paymentMethod, (newPaymentMethod) => {
  selectedPayment.value = newPaymentMethod;
});

let isReverting = false;

watch(selectedPayment, (newPaymentMethod, oldPaymentMethod) => {
  if (isReverting) {
    isReverting = false;
    return;
  }
  // Only allow payment method change if order is not processing
  if (!canModifyOrder()) {
    isReverting = true;
    selectedPayment.value = oldPaymentMethod;
    return;
  }
  const validMethod = paymentMethods.find(
    (method) => method.value === newPaymentMethod
  );
  if (validMethod) updatePaymentMethod(newPaymentMethod);
});
</script>

<style scoped>
.order-cta-container {
  display: flex;
  flex-direction: row;
  align-items: stretch;
  width: 100%;
  gap: 16px;
  min-height: 100%;
}

.payment-methods-container {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  padding: 0px;
  box-shadow: none;
}

.payment-title {
  padding-top: 15px;
  text-transform: uppercase;
}

.payment-methods .extra-checkbox {
  margin-bottom: 0px;
}

.payment-methods .extra-options {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
}

.payment-methods .extra-options label.extra-checkbox {
  display: flex;
  align-items: center;
  gap: 12px;
  border-radius: 6px;
  background: transparent;
  cursor: pointer;
  transition: background 0.2s;
  width: 100%;
  box-sizing: border-box;
}

.checkout-btn-wrapper {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

.btn.order-checkout-btn {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  box-sizing: border-box;
  margin: 0;
  padding: 0 24px;
}

.checkout-price {
  font-size: 20px;
}

@media (max-width: 700px) {
  .order-cta-container {
    flex-direction: column;
  }

  .payment-methods-container {
    flex: none;
    /* Reset for column layout */
    width: 100%;
    /* Full width in mobile */
  }

  .checkout-btn-wrapper {
    flex: none;
    /* Reset for column layout */
    width: 100%;
    /* Full width in mobile */
    margin-top: 16px;
  }

  .btn.order-checkout-btn {
    width: 100%;
    min-height: 100px;
    height: auto;
  }
}
</style>
